import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CategoryIcon, KpiCard, Skeleton, formatMoney, pickText } from '@aps/ui';
import { type AttentionKind } from '@aps/contracts';
import { ApiError, api } from '../lib/api.js';
import { messages } from '../i18n/messages.js';
import { useSession } from '../app/session-context.js';
import { type RootName } from '../app/navigation.js';
import { QueueError } from './WorkQueueScreen.js';

/**
 * Boshqaruv paneli — admin panel ochilganda birinchi ekran.
 *
 * Bu ekran KUNNI BOSHLASH uchun: xodim kirgach nima qilish kerakligini
 * ko'radi. Shuning uchun har signal BOSILADI va tegishli ekranga
 * olib boradi — raqam ko'rsatib, "endi o'zing top" demaydi.
 */

/** Har signal qaysi ekranga olib boradi va qanday og'irlikda. */
const ATTENTION: Record<AttentionKind, { tone: 'danger' | 'warn' | 'plain'; route: RootName }> = {
  NEW_ORDERS: { tone: 'danger', route: 'queue' },
  PENDING_PAYMENTS: { tone: 'danger', route: 'payments' },
  EMPTY_SEARCHES: { tone: 'warn', route: 'searchLog' },
  OUT_OF_STOCK: { tone: 'plain', route: 'products' },
};

/**
 * O'tish TEPADAN uzatiladi, `useNavigation()` bu yerda CHAQIRILMAYDI.
 *
 * `useNavigation` — oddiy `useState` hook, kontekst emas. Ekran ichida
 * chaqirilsa u O'ZINING alohida stackini quradi va `switchRoot`
 * hech qayerga olib bormaydi: signal bosiladi, ekran o'zgarmaydi.
 * (Shu holat aynan shunday bo'lgan va testda `useNavigation` mock
 * qilingani uchun ushlanmagan edi.)
 */
export function DashboardScreen({
  onNavigate,
}: {
  onNavigate: (root: RootName) => void;
}): ReactNode {
  const { me, locale } = useSession();
  const t = messages(locale);

  const data = useQuery({ queryKey: ['dashboard'], queryFn: api.dashboard });

  const header = (
    <header className="adm-page__header">
      <div>
        <h1 className="adm-page__title">{t.navDashboard}</h1>
        <p className="aps-muted">{t.dashboardSubtitle(me.roles[0] ?? '')}</p>
      </div>
    </header>
  );

  if (data.isPending) {
    return (
      <section className="adm-page">
        {header}
        <Skeleton height="320px" label={t.loading} />
      </section>
    );
  }

  if (data.isError) {
    return (
      <section className="adm-page">
        {header}
        <QueueError
          message={data.error instanceof ApiError ? data.error.text(locale) : t.errorTitle}
          retryLabel={t.retry}
          onRetry={() => void data.refetch()}
        />
      </section>
    );
  }

  /*
   * NOL bo'lgan signal KO'RSATILMAYDI.
   *
   * "0 ta muammo" degan qator ekranni to'ldiradi va haqiqiy signalni
   * ko'mib yuboradi. Server nolni ham qaytaradi (u tartibni biladi),
   * yashirish esa shu yerda.
   */
  const attention = data.data.attention.filter((row) => row.count > 0);

  return (
    <section className="adm-page">
      {header}

      <div className="adm-kpis">
        <KpiCard
          label={t.orders}
          value={String(data.data.orders)}
          hint={t.dashboardNewOrders(data.data.newOrders)}
          categoryKey="engine"
        />
        <KpiCard
          label={t.dashboardRevenue}
          value={formatMoney(data.data.revenue, locale)}
          hint={t.dashboardRevenueHint}
          categoryKey="electric"
        />
        {/*
          Foyda javobda BO'LMASLIGI mumkin — bu "ko'rsatilmaydi"
          degani, "nol" emas. Kartochka o'chgan holatda turadi va
          sabab yoziladi; butunlay yashirilsa xodim uni qidirardi.
        */}
        {data.data.grossProfit === undefined ? (
          <KpiCard
            label={t.dashboardProfit}
            value={t.dashboardClosed}
            hint={t.dashboardProfitHint}
            categoryKey="oils"
          />
        ) : (
          <KpiCard
            label={t.dashboardProfit}
            value={formatMoney(data.data.grossProfit, locale)}
            hint={t.dashboardMargin(marginPercent(data.data.revenue, data.data.grossProfit))}
            categoryKey="oils"
          />
        )}
        <KpiCard
          label={t.dashboardActiveProducts}
          value={String(data.data.activeProducts)}
          hint={t.dashboardDrafts(data.data.draftProducts)}
          categoryKey="tires"
        />
      </div>

      <div className="adm-dashboard__split">
        <section className="adm-panel">
          <h2 className="adm-panel__title">{t.dashboardAttention}</h2>
          <div className="adm-panel__body">
            {attention.length === 0 ? (
              <p className="aps-muted">{t.dashboardAllClear}</p>
            ) : (
              attention.map((row) => (
                <button
                  key={row.kind}
                  type="button"
                  className="adm-alert"
                  data-tone={ATTENTION[row.kind].tone}
                  onClick={() => {
                    onNavigate(ATTENTION[row.kind].route);
                  }}
                >
                  <b>{t.dashboardAlert(row.kind, row.count)}</b>
                  <span className="aps-muted">{t.dashboardAlertHint(row.kind)}</span>
                </button>
              ))
            )}
          </div>
        </section>

        <section className="adm-panel">
          <h2 className="adm-panel__title">{t.dashboardTop}</h2>
          <div className="adm-panel__body">
            {data.data.topProducts.length === 0 ? (
              <p className="aps-muted">{t.dashboardNoSales}</p>
            ) : (
              data.data.topProducts.map((product) => (
                <div key={product.id} className="adm-top-row" data-cat={product.categoryIconKey}>
                  <span className="adm-top-row__icon">
                    <CategoryIcon iconKey={product.categoryIconKey} size={17} />
                  </span>
                  <span className="adm-top-row__name">
                    {pickText(product.nameUz, product.nameRu, locale)}
                  </span>
                  <b className="adm-top-row__count">{product.soldCount}</b>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </section>
  );
}

/**
 * Marja foizi.
 *
 * Tushum NOL bo'lsa foiz ANIQLANMAGAN — nolga bo'linish. Bunday
 * holatda `0` qaytariladi va klient "marja 0%" deb yozadi: bu rost,
 * chunki sotuv bo'lmagan.
 */
function marginPercent(revenue: string, profit: string): number {
  const total = BigInt(revenue);
  if (total === 0n) return 0;
  return Number((BigInt(profit) * 100n) / total);
}
